import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  CircularProgress,
  Box,
} from "@mui/material";
import {
  doc,
  onSnapshot,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../api/firebase";
import { useAuth } from "../context/auth";

/**
 * =========================================================
 * BuildIdGuard（重要：運用コメント）
 * =========================================================
 *
 * 【このコンポーネントの役割】
 * - Firestore / Functions / API にアクセスする「前」に、
 *   アプリのビルドバージョンを UI レベルで検査する。
 *
 * - Firebase Rules で拒否する「前」に UI で止めることで、
 *   permission-denied・白画面・例外多発を防ぐ。
 *
 * - allow のときだけ users/{uid}.buildId（通行証）を発行して、
 *   その後の画面（例：TopPageMusicSection）を解放する。
 *
 *
 * 【必ず守るデプロイ順】
 *
 * ① BuildIdGuard を含むアプリコードを先にデプロイ
 * ② Firestore: appFlags/currentBuild を更新
 * ③ ユーザーが UI（Dialog）で止まる / 通ることを確認
 * ④ 必要であれば最後に Firestore Rules を締める
 *
 * ※ ②→① の順でやると、旧ビルドが DB に触って事故る
 *
 *
 * 【よくある運用パターン】
 *
 * ● 単に「最新版に上げてほしい」だけ（通常はこれ）
 *   - requiredBuildId(requiredBuildCode) を上げる
 *   - rollout / minSupported は使わない
 *
 * ● 絶対に使わせたくない致命的バグがある（緊急封鎖）
 *   - minSupportedBuildId(minSupportedBuildCode) を上げる
 *   - この値未満は UI 上で完全ブロック（block）
 *
 * ● 段階的に告知したい（通常は不要）
 *   - rollout.enabled = true
 *   - rollout.percent を使う（UIDを 0-99 に割り当てて割合制御）
 *
 *
 * 【rollout.enabled / rollout.percent の意味】
 *
 * - requiredBuildCode 未満のユーザーに対して
 *   「アップデート促し（force）」を段階的に出すための仕組み。
 *
 * - rollout.enabled = false の場合：
 *   requiredBuildCode 未満でも “allow” 扱い（＝促さない）
 *
 * - rollout.enabled = true の場合：
 *   requiredBuildCode 未満のうち、
 *   uidToBucket(uid) < rollout.percent の人だけ “force”
 *   （例：percent=30 → 約30%だけダイアログで促す）
 *
 *
 * 【minSupportedBuildId の意味】
 *
 * - これ未満は “block” 扱い。
 * - 「あとで」ボタンも出さない（閉じられない）。
 * - 致命的バグ/セキュリティ問題など “絶対停止” の用途。
 *
 *
 * 【なぜ UI で止めてから Rules を締めるのか】
 *
 * - Rules で直接止めると：
 *   ・permission-denied
 *   ・React の想定外例外
 *   ・白画面
 *   ・ユーザーは理由が分からない
 *
 * - UI で止めれば：
 *   ・理由を説明できる
 *   ・更新導線を出せる
 *   ・2〜5万人規模でも安全に制御できる
 *
 * =========================================================
 */

/**
 * ==============================
 * アプリ側の現在ビルドID
 * ==============================
 * ★ デプロイ時にここだけを書き換える
 */
export const APP_BUILD_ID = "2.9";

/**
 * "2.0" -> 200 / "3.1" -> 310
 * buildId は文字列、比較は数値（事故防止）
 */
const buildCodeFromId = (id) => {
  const s = String(id ?? "").trim();
  if (!s) return 0;

  const [majorRaw, minorRaw = "0"] = s.split(".");
  const major = Number(majorRaw);
  const minor = Number(String(minorRaw).padEnd(2, "0").slice(0, 2));

  if (!Number.isFinite(major) || !Number.isFinite(minor)) return 0;
  return major * 100 + minor;
};

/**
 * rollout.percent 用
 * UID を安定した 0–99 に割り当てる
 */
const uidToBucket = (uid) => {
  if (!uid) return 0;
  let hash = 0;
  for (let i = 0; i < uid.length; i++) {
    hash = (hash * 31 + uid.charCodeAt(i)) >>> 0;
  }
  return hash % 100;
};

/**
 * ==============================
 * BuildIdGuard 本体
 * ==============================
 *
 * props:
 * - children: 通過したら表示するコンテンツ
 * - onReady: allow 判定＆通行証発行完了後に呼ばれるコールバック（任意）
 */
export default function BuildIdGuard({ children, onReady }) {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [flag, setFlag] = useState(null);
  const [open, setOpen] = useState(false);

  const myBuildCode = useMemo(() => buildCodeFromId(APP_BUILD_ID), []);

  // ------------------------------
  // Firestore: appFlags/currentBuild
  // ------------------------------
  useEffect(() => {
    const ref = doc(db, "appFlags", "currentBuild");

    const unsub = onSnapshot(
      ref,
      (snap) => {
        setFlag(snap.exists() ? snap.data() : null);
        setLoading(false);
      },
      (err) => {
        console.warn("[BuildIdGuard] snapshot error:", err);
        setFlag(null);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  // ------------------------------
  // 判定ロジック
  // ------------------------------
  const decision = useMemo(() => {
    // flag が無い / 取得失敗なら “止めない” 方針（運用はここを変えても良い）
    if (!flag) return { action: "allow" };

    // 未ログインは allow（ログイン前に止めたくない場合）
    if (!user?.uid) return { action: "allow" };

    const uid = user.uid;

    const {
      requiredBuildCode = 0,
      minSupportedBuildCode = 0,
      rollout = {},
    } = flag;

    const {
      enabled = false,
      percent = 100,
      allowUids = [],
      denyUids = [],
    } = rollout || {};

    /**
     * denyUids
     * - 常に除外（運営・検証用）
     */
    if (denyUids.includes(uid)) {
      return { action: "allow" };
    }

    /**
     * allowUids
     * - 常に対象（運営・検証用）
     */
    if (allowUids.includes(uid)) {
      return { action: "force" };
    }

    /**
     * minSupported 未満
     * - 絶対に使わせない（block）
     */
    if (myBuildCode < minSupportedBuildCode) {
      return { action: "block" };
    }

    /**
     * required 未満
     * - rollout.enabled の場合のみ促す
     */
    if (myBuildCode < requiredBuildCode) {
      if (!enabled) return { action: "allow" };

      const bucket = uidToBucket(uid);
      if (bucket < percent) {
        return { action: "force" };
      }
    }

    return { action: "allow" };
  }, [flag, user?.uid, myBuildCode]);

  // ------------------------------
  // Dialog 表示制御
  // ------------------------------
  useEffect(() => {
    if (decision.action === "force" || decision.action === "block") {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [decision.action]);

  // ------------------------------
  // allow のとき「通行証」を発行してから onReady を呼ぶ
  // ------------------------------
  useEffect(() => {
    if (loading) return;
    if (decision.action !== "allow") return;
    if (!user?.uid) {
      // 未ログイン allow の場合は、単に ready 扱いにする
      onReady?.();
      return;
    }

    let canceled = false;

    const issuePass = async () => {
      try {
        const userRef = doc(db, "users", user.uid);
        const snap = await getDoc(userRef);
        const data = snap.exists() ? snap.data() : {};

        // すでに最新なら書かない（無駄な write を避ける）
        if (data?.buildCode === myBuildCode && data?.buildId === APP_BUILD_ID) {
          if (!canceled) onReady?.();
          return;
        }

        await setDoc(
          userRef,
          {
            buildId: APP_BUILD_ID,
            buildCode: myBuildCode,
            buildUpdatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        if (!canceled) onReady?.();
      } catch (e) {
        // ここで失敗しても「アプリを止める」かは運用方針次第
        // 今回は “止めない” 方針（ただし Rules で buildId を要求するなら止めた方が良い）
        console.warn("[BuildIdGuard] failed to write users buildId/buildCode:", e);
        if (!canceled) onReady?.();
      }
    };

    issuePass();

    return () => {
      canceled = true;
    };
  }, [loading, decision.action, user?.uid, myBuildCode, onReady]);

  // ------------------------------
  // 表示文言
  // ------------------------------
  const message = flag?.message || {};
  const title = message.title || "アップデートのお願い";
  const body =
    message.body ||
    "最新版へ更新してください。更新後、再度ご利用いただけます。";
  const primaryText = message.primaryText || "更新する";
  const secondaryText = message.secondaryText || "あとで";

  // ------------------------------
  // 初期ローディング
  // ------------------------------
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // ------------------------------
  // Render
  // ------------------------------
  return (
    <>
      {children}

      <Dialog
        open={open}
        disableEscapeKeyDown
        onClose={() => {
          // block の場合は閉じられない
          if (decision.action !== "block") {
            setOpen(false);
          }
        }}
      >
        <DialogTitle>{title}</DialogTitle>

        <DialogContent>
          <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
            {body}
          </Typography>
        </DialogContent>

        <DialogActions>
          {decision.action !== "block" && (
            <Button onClick={() => setOpen(false)}>
              {secondaryText}
            </Button>
          )}

          <Button
            variant="contained"
            onClick={() => {
              // ★ 実際の App Store / Web URL に置き換える
              window.location.href = "https://mastermindmembers.net/";
            }}
          >
            {primaryText}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
